import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandColors } from '@hallmark/web.styles.colors';
import { useAnalyticsContext } from '../../../context/analytics-context';
import {
  setIsImageUploadToPhotoZoneDrawerOpen,
  setIsTextDrawerOpen,
  setIsWamDrawerOpen,
  useAppContext,
} from '../../../context/app-context';
import { setSelectedPhotoZone, useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { usePhotoTextZoneControls } from '../../../features/card-editor/hooks/use-photo-text-zone-controls';
import { useTextArea } from '../../../features/card-editor/hooks/use-text-area';
import { handlePhototextzoneSelection } from '../../../features/card-editor/utils/handle-phototextzone-selection';
import { useFeatureFlags, useIsSignAndSend } from '../../../hooks';
import { ActionType, useDatadog } from '../../../hooks/use-datadog';
import colorsList from '../../../styles/util.scss';
import {
  addUserTextHandlers,
  CanvasDataTypes,
  convertPointToPixel,
  getFontidByUrlName,
  getGroupedItemByName,
} from '../../../utils';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { parseTextAction } from '../../../utils/parse-text-action';
import { getTextSettings, removeEditableAreaButtons } from '../../card-editor/utils';
import { ResetTextData } from '../card-face-types';
import { createTextElement } from '../utils/create-text-element';

type UseCanvasMouseUpProps = {
  cardIndex: number;
  fabric: typeof import('fabric').fabric;
  editingHybridBox: React.MutableRefObject<boolean>;
  setIsResetTextModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useCanvasMouseUp = ({
  cardIndex,
  fabric,
  editingHybridBox,
  setIsResetTextModalOpen,
}: UseCanvasMouseUpProps) => {
  const { initializedDataState } = useInitializationDataContext();
  const { cardState, cardDispatch } = useCardContext();
  const { appDispatch } = useAppContext();
  const { trackPhotoUploadMethod } = useAnalyticsContext();
  const { t } = useTranslation();
  const { addCustomTextArea } = useTextArea();
  const { IS_TEXTBOX_OVERLAY_ENABLED, IS_POD_USER_TEXTBOX_OVERLAY_ENABLED, IS_SNS_TEXTBOX_OVERLAY_ENABLED } =
    useFeatureFlags();
  const { handleTypeAMessage, handleAddHandwriting } = usePhotoTextZoneControls();
  const { DISPLAY_ZOOM_DEV, SAS_DYNAMIC_BUTTONS } = useFeatureFlags();
  const { addAction, textAction } = useDatadog();
  const isSignAndSend = useIsSignAndSend();
  const [resetTextData, setResetTextData] = useState<ResetTextData>({
    canvas: null,
    zoneName: '',
  });
  const { isUK } = initializedDataState;
  const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };

  const handleMouseUp = ({ options, canvasRef, canvas, overlayTextAreaRef }) => {
    // If nothing was clicked on the canvas and the overlay is open, close the overlay for sign and send
    if (!options?.target && isSignAndSend && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED) {
      const isOverlayVisible = overlayTextAreaRef?.current?.isOverlayTextareaVisible();
      if (isOverlayVisible) {
        // Blur the overlay textarea to close it
        overlayTextAreaRef.current?.onTextareaBlur();
      }
      canvas?.discardActiveObject();
      canvas?.renderAll();
      return;
    }

    //  handles the uploading image for a photo zone
    if (
      options?.target?.data?.type === CanvasDataTypes.PhotoTextZone &&
      isSignAndSend &&
      IS_SNS_TEXTBOX_OVERLAY_ENABLED
    ) {
      const originalTextbox = getGroupedItemByName(CanvasDataTypes.PhotoZoneTextbox, options.target) as fabric.Textbox;
      canvas?.setActiveObject(options.target);
      if (originalTextbox.text !== '') {
        setIsTextDrawerOpen(appDispatch);
        originalTextbox.enterEditing();
      }
    }

    if (options?.target?.data?.type === CanvasDataTypes.PhotoZoneButton) {
      setSelectedPhotoZone(cardDispatch, options.target.data.photoZoneId);
      setIsImageUploadToPhotoZoneDrawerOpen(appDispatch);
      trackPhotoUploadMethod('camera icon');
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddTextButton) {
      // Adds an editable text box to the user zone
      if (canvas && canvasRef.current) {
        if (IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED) {
          const currentCardFace = cardState.cardFacesList[`${cardIndex}`];
          addCustomTextArea({
            canvas: canvasRef,
            cardFace: currentCardFace,
            onError: () => {
              throw new Error('Error adding custom text area');
            },
          });
        } else {
          const INITIAL_TEXT_MESSAGE = `${t('cardEditor.userTextDefaultMessage')}`;
          const cardFaceClipPath = getCardFaceClipPath(cardState.cardFacesList[`${cardIndex}`], 0);
          const defaultFallbackColor = isUK
            ? colorsList[BrandColors.CharcoalGrey]
            : initializedDataState.defaultColor || colorsList[BrandColors.Black];
          const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
          const defaultFontSize = convertPointToPixel(
            initializedDataState.data?.variables.template_data.DefaultTextArea.FontSize || 50,
          );
          defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
            defaultTextAreaSettings?.FontFamily || '',
            initializedDataState.data?.font_collection.fonts,
          );

          const defaultTextSettings = getTextSettings({
            cardState,
            color: defaultColor,
            defaultFontSize,
            isUK,
            defaultOptions: defaultTextAreaSettings,
            name: CanvasDataTypes.UserText,
          });
          const width = cardFaceClipPath.width;

          const initialTextMessage = defaultTextSettings.Text || INITIAL_TEXT_MESSAGE;
          const textElement = createTextElement({
            width,
            fabric,
            initialTextMessage,
            isUK,
            defaultTextSettings,
            cardFaceClipPath,
            cardState,
          });

          addUserTextHandlers(textElement, initialTextMessage, defaultColor, canvasRef.current);
          textAction(ActionType.ADD_TEXT, parseTextAction(textElement));
          canvasRef.current.add(textElement);
          canvasRef.current.setActiveObject(textElement);
        }
      }
      removeEditableAreaButtons(canvas);
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddWamButton) {
      setIsWamDrawerOpen(appDispatch);
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneTextButton) {
      editingHybridBox.current = true;
      handleTypeAMessage({
        canvas,
        button: options?.target,
      });
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneWAMButton) {
      handleAddHandwriting({
        canvas,
        button: options?.target,
      });
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneResetButton) {
      if (canvas) {
        setIsResetTextModalOpen(true);
        setResetTextData({
          canvas: canvas,
          zoneName: options?.target?.data?.linkedZoneName,
        });
      }
    } else if (
      options?.target?.data?.type === CanvasDataTypes.PhotoTextZone &&
      !(isSignAndSend && IS_SNS_TEXTBOX_OVERLAY_ENABLED)
    ) {
      const photoTextZone = options.target as fabric.Group;
      handlePhototextzoneSelection({
        photoTextZone,
        canvas,
        isDisplayZoomEnabled: DISPLAY_ZOOM_DEV,
        isSnSButtonsEnabled: SAS_DYNAMIC_BUTTONS,
      });
    } else if (
      options?.target?.data?.type === CanvasDataTypes.PhotoZoneImage ||
      options?.target?.data?.type === CanvasDataTypes.UserImage
    ) {
      addAction('select-image', {
        image: options.target.data,
        cardFace: cardState.cardFacesList[`${cardState.activeCardIndex}`]?.type ?? null,
        zoneId: options.target.data.photoZoneId || null,
      });
    }
  };

  return { resetTextData, handleMouseUp };
};
